/**
 * Customer app shell.
 *
 * Provider order matters:
 *   Appearance  outermost, so every error message below is themed.
 *   Locale      next, so every message below is translated.
 *   Clock       one timer for every countdown in the app.
 *   Toast       needs both, and reports the results of actions taken deeper in.
 *   Session     gates the tree: no anonymous session means no ordering, so
 *               nothing that could place an order is mounted until it resolves.
 *   Cart        persisted per device, independent of the menu currently shown.
 *
 * `PortalHost` is mounted once, inside every provider, because the overlay
 * components render through it rather than into their own parent.
 */

import { useCallback, useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { View } from 'react-native';
import {
  AppearanceProvider,
  ClockProvider,
  LocaleProvider,
  PortalHost,
  ToastProvider,
  useLocale,
  useT,
} from '@yummy/ui-rnr';

import { CartProvider } from './app/cart';
import { SessionProvider } from './app/session';
import { FullScreenMessage } from './components/FullScreenMessage';
import { CheckoutScreen } from './screens/CheckoutScreen';
import { HistoryScreen } from './screens/HistoryScreen';
import { LandingScreen } from './screens/LandingScreen';
import { MenuScreen } from './screens/MenuScreen';
import { OrderScreen } from './screens/OrderScreen';
import { CircleHelp } from 'lucide-react-native';


/** Reads the printed table token from the URL and renders its menu. */
function TableRoute() {
  const { token = '' } = useParams<{ token: string }>();
  return <MenuScreen source={{ value: token, kind: 'token' }} />;
}

/** Reads the restaurant slug from the URL and renders its menu. */
function SlugRoute() {
  const { slug = '' } = useParams<{ slug: string }>();
  return <MenuScreen source={{ value: slug, kind: 'slug' }} />;
}

/** The 404 state, which needs the translator so it lives inside the providers. */
function NotFound() {
  const { t } = useLocale();
  return (
    <FullScreenMessage
      icon={CircleHelp}
      title={t('common.notFound')}
      message={t('customer.menu.loadFailedHint')}
      actionLabel={t('customer.landing.findOrder')}
      onAction={() => {
        window.location.href = '/';
      }}
    />
  );
}

/**
 * Checkout as a full-screen overlay.
 *
 * Fixed over the screen rather than routed into it, so the menu underneath — its
 * scroller, its tab row, the section it was left on — is never unmounted. There
 * is no scroll to restore because nothing let go of it.
 *
 * It behaves as a dialog: it takes focus on the way in, keeps Tab inside it,
 * closes on Escape, and hands focus back on the way out. The route behind it is
 * still the menu, so the checkout's own back control closes the overlay rather
 * than leaving the restaurant.
 *
 * @returns The overlay and the checkout screen inside it.
 */
function CheckoutOverlay() {
  const t = useT();
  const navigate = useNavigate();
  const container = useRef<View>(null);
  const close = useCallback(() => navigate(-1), [navigate]);

  // Web-only, like the other DOM-measured screens here: focus and Escape are
  // browser concerns, and a keyboard is the case a full-screen route change would
  // otherwise have handled for free.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const node = container.current as unknown as HTMLElement | null;
    if (!node) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    node.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      // A sheet opened from inside owns the keyboard while it is up; closing the
      // checkout under it would lose the customer's place.
      const sheetOpen = document.querySelector('[role="dialog"][data-state="open"]') !== null;
      if (event.key === 'Escape') {
        if (sheetOpen) return;
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab' || sheetOpen) return;

      const focusables = Array.from(
        node.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.getClientRects().length > 0);
      if (focusables.length === 0) return;

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      // Focus outside — on the body, say, after a sheet closed: pull it back in
      // rather than let Tab walk into the menu behind the overlay.
      if (!node.contains(active) || active === node) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused?.focus?.();
    };
  }, [close]);

  return (
    <View
      ref={container}
      role="dialog"
      aria-modal
      aria-label={t('customer.checkout.screenTitle')}
      // Focusable by script, not by Tab: the trap above is what lets Tab reach it.
      tabIndex={-1}
      // Bare, and full-screen from the instant it mounts: the menu behind it is
      // covered, so a second tap during the slide cannot push a second checkout.
      className="fixed inset-0 z-50">
      {/* The visible sheet. It slides in at once and shows the checkout's
          skeleton while the data is on its way, so the dialog arrives with
          structure instead of an empty frame. `will-change-transform` keeps the
          slide on the compositor. */}
      <View className="bg-background absolute inset-0 flex animate-in slide-in-from-bottom duration-300 ease-sheet will-change-transform">
        <CheckoutScreen overlay />
      </View>
    </View>
  );
}

/**
 * The route table, with checkout overlaid on the screen it was opened from.
 *
 * Navigating to checkout carries the menu's location in `state.backgroundLocation`.
 * When that is present the main routes keep rendering the menu from it and the
 * checkout is mounted on top; a refresh or a shared link has no state, so the
 * same checkout route resolves normally as a page of its own.
 *
 * @returns The routes and, when checkout was opened in-app, the overlay.
 */
function AppRoutes() {
  const location = useLocation();
  const background = (location.state as { backgroundLocation?: Location } | null)
    ?.backgroundLocation;

  return (
    <>
      <Routes location={background ?? location}>
        <Route path="/" element={<LandingScreen />} />

        {/* Printed table sticker: carries a signed YMT1 token. */}
        <Route path="/t/:token" element={<TableRoute />} />
        {/* Link shared by the restaurant, or typed by the customer. */}
        <Route path="/r/:slug" element={<SlugRoute />} />
        {/* Reached directly, or overlaid when opened from the menu above. */}
        <Route path="/r/:slug/checkout" element={<CheckoutScreen />} />

        {/* Order tracking. The static segment must be matched first. */}
        <Route path="/order/code/:code" element={<OrderScreen />} />
        <Route path="/order/:orderId" element={<OrderScreen />} />

        <Route path="/orders" element={<HistoryScreen />} />

        <Route path="*" element={<NotFound />} />
      </Routes>

      {background ? (
        <Routes>
          <Route path="/r/:slug/checkout" element={<CheckoutOverlay />} />
        </Routes>
      ) : null}
    </>
  );
}

/**
 * Mounts the customer app routes inside its providers.
 *
 * @returns The provider stack and the route table.
 */
export function App() {
  return (
    <AppearanceProvider storageKey="yummy.customer.appearance">
      <LocaleProvider storageKey="yummy.customer.locale">
        <ClockProvider>
          <ToastProvider>
            <SessionProvider>
              <CartProvider>
                  <BrowserRouter>
                    <AppRoutes />
                  </BrowserRouter>
                  <PortalHost />
              </CartProvider>
            </SessionProvider>
          </ToastProvider>
        </ClockProvider>
      </LocaleProvider>
    </AppearanceProvider>
  );
}
